import React, { createContext, useState, useEffect } from "react";
import { ethers } from "ethers";
import WalletSelectModal from "../components/WalletSelectModel";
import { EthereumProvider } from "@walletconnect/ethereum-provider";

export const WalletContext = createContext();

const MEZO_TESTNET = {
  chainId: "0x7B7B",
  chainName: "Mezo Testnet",
  nativeCurrency: { name: "MEZO", symbol: "MEZO", decimals: 18 },
  rpcUrls: ["https://rpc.test.mezo.org"],
  blockExplorerUrls: ["https://explorer.test.mezo.org"],
};

export const WalletProvider = ({ children }) => {
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [isCorrectNetwork, setIsCorrectNetwork] = useState(false);
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [walletConnectProvider, setWalletConnectProvider] = useState(null); // NEW

  const isMobile = () => /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  
  const isMetaMaskMobileApp = () => {
    return (
      typeof window !== "undefined" &&
      window.ethereum &&
      (window.ethereum.isMetaMask || window.ethereum._metamask)
    );
  };

  const checkNetwork = async (web3Provider) => {
    try {
      const network = await web3Provider.getNetwork();
      const correct = network.chainId === 31611n;
      setChainId(network.chainId);
      setIsCorrectNetwork(correct);
      return correct;
    } catch (err) {
      console.error("Network check failed:", err);
      return false;
    }
  };

  const switchToMezoTestnet = async () => {
    if (!window.ethereum) return alert("Install MetaMask!");

    try {
      await window.ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: MEZO_TESTNET.chainId }],
      });
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      return true;
    } catch (err) {
      if (err.code === 4902) {
        try {
          await window.ethereum.request({
            method: "wallet_addEthereumChain",
            params: [MEZO_TESTNET],
          });
          await new Promise(resolve => setTimeout(resolve, 1000));
          return true;
        } catch (addErr) {
          console.error("Failed to add network:", addErr);
          alert("Failed to add Mezo network. Please add it manually in MetaMask.");
          return false;
        }
      } else {
        console.error("Failed to switch network:", err);
        alert("Failed to switch network. Please switch manually in MetaMask.");
        return false;
      }
    }
  };

  const handleAccountSelect = async (acc) => {
    setSelectedWallet(acc);
    setShowModal(false);
    
    if (provider) {
      try {
        const web3Signer = await provider.getSigner();
        setSigner(web3Signer);
      } catch (err) {
        console.error("Failed to get signer:", err);
      }
    }
  };
  
  const handleModalClose = () => setShowModal(false);

  /**
   * NEW: Connect via WalletConnect
   */
  const connectViaWalletConnect = async () => {
    try {
      console.log("🔗 Initializing WalletConnect...");

      const wcProvider = await EthereumProvider.init({
        projectId: "89bea844a456db7f6bc67d09fac07c6e",
        chains: [31611],
        showQrModal: true,
        rpcMap: {
          31611: "https://rpc.test.mezo.org"
        },
        metadata: {
          name: "BitSpend",
          description: "Self-custodial payment card",
          url: window.location.origin,
          icons: [`${window.location.origin}/logo192.png`]
        }
      });

      console.log("📱 Showing QR modal...");
      
      // Enable will show QR modal
      await wcProvider.enable();
      
      console.log("✅ WalletConnect connected");

      // Create ethers provider
      const web3Provider = new ethers.BrowserProvider(wcProvider);
      setProvider(web3Provider);
      setWalletConnectProvider(wcProvider);

      // Get accounts
      const accounts = await web3Provider.listAccounts();
      
      if (!accounts || accounts.length === 0) {
        throw new Error("No accounts from WalletConnect");
      }

      const address = accounts[0].address;
      setSelectedWallet(address);

      // Get signer
      const web3Signer = await web3Provider.getSigner();
      setSigner(web3Signer);

      // Check network
      await checkNetwork(web3Provider);

      console.log("✅ Connected via WalletConnect:", address);
      
      return address;

    } catch (err) {
      console.error("❌ WalletConnect failed:", err);
      throw err;
    }
  };

  /**
   * Existing connectWallet (with WalletConnect option added)
   */
  const connectWallet = async (useWalletConnect = false) => {
    if (isConnecting) {
      console.log("Connection already in progress...");
      return null;
    }

    setIsConnecting(true);
    setConnectionError(null);

    try {
      // NEW: If explicitly requesting WalletConnect
      if (useWalletConnect) {
        const address = await connectViaWalletConnect();
        setIsConnecting(false);
        return address;
      }

      // Existing MetaMask flow
      const injected = window.ethereum;

      if (!injected) {
        throw new Error("No wallet found. Please install MetaMask or use WalletConnect.");
      }

      console.log("Connecting via MetaMask...");
      
      const accounts = await injected.request({ 
        method: "eth_requestAccounts" 
      });
      
      if (!accounts || accounts.length === 0) {
        throw new Error("No accounts returned from MetaMask");
      }

      const web3Provider = new ethers.BrowserProvider(injected);
      setProvider(web3Provider);

      if (accounts.length > 1) {
        setAvailableAccounts(accounts);
        setShowModal(true);
        setIsConnecting(false);
        return null;
      }

      const web3Signer = await web3Provider.getSigner();
      setSigner(web3Signer);
      setSelectedWallet(accounts[0]);

      const correct = await checkNetwork(web3Provider);
      if (!correct) {
        await switchToMezoTestnet();
      }

      console.log("Connected successfully:", accounts[0]);
      return accounts[0];
      
    } catch (err) {
      console.error("Wallet connection failed:", err);
      
      const errorMessage = err.message || "Connection failed";
      setConnectionError(errorMessage);
      
      if (err.code === 4001) {
        alert("Connection rejected. Please approve the connection.");
      } else {
        alert(`Connection failed: ${errorMessage}`);
      }
      
      return null;
      
    } finally {
      setIsConnecting(false);
    }
  };

  /**
   * NEW: Disconnect WalletConnect
   */
  const disconnectWallet = async () => {
    try {
      if (walletConnectProvider) {
        await walletConnectProvider.disconnect();
        setWalletConnectProvider(null);
      }
      
      setSelectedWallet(null);
      setSigner(null);
      setProvider(null);
      setChainId(null);
      setIsCorrectNetwork(false);
      
      console.log("✅ Wallet disconnected");
    } catch (err) {
      console.error("❌ Disconnect failed:", err);
    }
  };

  useEffect(() => {
    const init = async () => {
      const injected = window.ethereum;
      
      if (!injected) {
        console.log("No Web3 provider detected");
        return;
      }

      try {
        const web3Provider = new ethers.BrowserProvider(injected);
        setProvider(web3Provider);

        const accounts = await injected.request({ method: "eth_accounts" });
        
        if (accounts && accounts.length > 0) {
          console.log("Found existing connection:", accounts[0]);
          
          setSelectedWallet(accounts[0]);
          
          const web3Signer = await web3Provider.getSigner();
          setSigner(web3Signer);
          
          await checkNetwork(web3Provider);
        }

        injected.on("accountsChanged", async (accounts) => {
          console.log("Accounts changed:", accounts);
          
          if (accounts && accounts.length > 0) {
            setSelectedWallet(accounts[0]);
            
            try {
              const web3Signer = await web3Provider.getSigner();
              setSigner(web3Signer);
            } catch (err) {
              console.error("Failed to get signer after account change:", err);
            }
          } else {
            setSelectedWallet(null);
            setSigner(null);
          }
        });

        injected.on("chainChanged", (chainId) => {
          console.log("Chain changed:", chainId);
          window.location.reload();
        });
        
      } catch (err) {
        console.error("Provider initialization failed:", err);
      }
    };

    init();

    return () => {
      if (window.ethereum) {
        window.ethereum.removeAllListeners("accountsChanged");
        window.ethereum.removeAllListeners("chainChanged");
      }
    };
  }, []);

  return (
    <WalletContext.Provider
      value={{
        selectedWallet,
        provider,
        signer,
        chainId,
        isCorrectNetwork,
        isConnecting,
        connectionError,
        connectWallet,
        disconnectWallet, // NEW
        switchToMezoTestnet,
        MEZO_TESTNET,
      }}
    >
      {children}
      {showModal && (
        <WalletSelectModal
          accounts={availableAccounts}
          onSelect={handleAccountSelect}
          onClose={handleModalClose}
        />
      )}
    </WalletContext.Provider>
  );
};
